// src/pages/philosophy.tsx
import Head from "next/head";
import Link from "next/link";
import {
    Shield,
    BookOpen,
    Brain,
    TrendingUp,
    Users,
    CheckCircle,
    ArrowRight,
    ChevronDown,
    Scale,
} from "lucide-react";
import React, { useState } from "react";
import { getBrandDNA, BrandDNA } from "@/lib/brandDNA";
import {
    MONETISATION_TAGLINES,
    MONETISATION_REFUSALS,
    PERMITTED_MONETISATION_LAYERS,
} from "@/content/brand/monetisation";

export async function getStaticProps() {
    const dna = getBrandDNA();
    return {
        props: {
            dna,
        },
    };
}

const SITE_URL = "https://www.durmah.ai";
const canonicalUrl = `${SITE_URL}/philosophy`;

const FAQ_ITEMS = [
    {
        q: "Is Durmah an AI study companion?",
        a: "Not in the traditional sense. Durmah is a structured study companion: it provides frameworks, guided prompts, and feedback loops so that you do the thinking. The goal is to develop your reasoning ability, not replace it. Think of it as a practice partner that holds you to a high standard.",
    },
    {
        q: "Why not just use ChatGPT, Gemini, Claude, or other AI tools directly?",
        a: "You can, and many students do. General-purpose AI tools are powerful, but they are designed to produce output, not to build your legal reasoning. Durmah is structured differently: it centres skill-building over shortcutting, wraps every interaction in ethical guardrails, and keeps the student as the author at every stage. Raw AI output can be fluent and confident while being structurally weak or legally imprecise. Those are precisely the qualities that examiners notice. Our system optimises for you becoming a stronger legal thinker, not for generating a faster answer. AI is quiet support here. You remain the advocate.",
    },
    {
        q: "Will the AI write my assignments for me?",
        a: "No, and that's by design. Academic integrity is built into how Durmah works. It helps you plan, structure, and self-check your reasoning, but the writing is always yours. We believe that shortcuts in a law degree are shortcuts to a diminished career.",
    },
    {
        q: "How does this help me learn faster?",
        a: "Speed comes from clarity. Durmah gives you structured frameworks so you don't waste hours staring at a blank page. You spend your energy on the substance (applying the law, building arguments, practising precision) rather than figuring out where to start.",
    },
    {
        q: "How do you handle academic integrity?",
        a: "We treat it as a non-negotiable. Our platform does not provide model answers or generate submissions on your behalf. Every interaction is designed to prompt reflection, not shortcut it. We also provide a dedicated Academic Integrity Guide to help students understand the boundaries clearly.",
    },
    {
        q: "Does this work outside of one specific university?",
        a: "Yes. While the platform is first deployed for law students, the underlying philosophy (student skill-building, ethical learning, structured practice) is universal. We are expanding to additional universities and degree programmes steadily.",
    },
    {
        q: "What makes this different from other AI study tools?",
        a: "We don't compete on model power. We compete on philosophy. Most AI tools centre the AI as the hero. We centre the student. Every feature decision asks: 'Does this build the student's own capability?' If not, we don't build it.",
    },
];

function FAQItem({ q, a }: { q: string; a: string }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border border-gray-200 dark:border-white/10 rounded-2xl overflow-hidden transition-all duration-300 hover:border-gray-300 dark:hover:border-white/20">
            <button
                className="w-full text-left px-8 py-6 flex items-center justify-between gap-4"
                onClick={() => setOpen(!open)}
                aria-expanded={open}
            >
                <span className="text-base md:text-lg font-bold text-gray-900 dark:text-white">
                    {q}
                </span>
                <ChevronDown
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <div className="px-8 pb-6">
                    <p className="text-gray-600 dark:text-white/70 leading-relaxed text-base font-medium">
                        {a}
                    </p>
                </div>
            )}
        </div>
    );
}

export default function PhilosophyPage({ dna }: { dna: BrandDNA }) {
    return (
        <>
            <Head>
                <title>Students First | Legal Learning for Law Students | Durmah</title>
                <meta
                    name="description"
                    content="Why Durmah puts law students first. Our platform is built on student-first principles: ethical AI, structured legal learning, and skill-building that keeps law students in control of their education, not dependent on it."
                />
                <meta
                    property="og:title"
                    content="Students First | Legal Learning for Law Students | Durmah"
                />
                <meta property="og:type" content="article" />
                <meta property="og:site_name" content="Durmah" />
                <meta
                    property="og:description"
                    content="We refuse to make AI the hero. Here's why student skill, confidence, and integrity matter more than any model upgrade."
                />
                <meta key="route-og-url" property="og:url" content={canonicalUrl} />
                <link key="route-canonical" rel="canonical" href={canonicalUrl} />
                <meta name="robots" content="index, follow" />
            </Head>

            <main className="bg-white dark:bg-[#020617] text-gray-900 dark:text-white">
                {/* ── HERO ── */}
                <section className="py-28 md:py-40 px-6 max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-[10px] font-black uppercase tracking-[0.2em] border border-indigo-100 dark:border-indigo-500/20 mb-8">
                        <Shield size={14} />
                        Our Philosophy
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.05] mb-8">
                        {dna.philosophyLine}
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-500 dark:text-white/70 leading-relaxed max-w-2xl mx-auto font-medium">
                        {dna.positioning}
                    </p>
                    <p className="text-base md:text-lg text-gray-400 dark:text-white/50 leading-relaxed max-w-xl mx-auto font-medium mt-5">
                        {dna.productEthos}
                    </p>
                </section>

                {/* ── INTRO ── */}
                <section className="py-16 md:py-24 border-t border-gray-100 dark:border-white/[0.05]">
                    <div className="max-w-3xl mx-auto px-6 space-y-8">
                        <p className="text-lg md:text-xl text-gray-700 dark:text-white/80 leading-relaxed font-medium">
                            There is a temptation in the AI industry to win by claiming
                            supremacy: faster, smarter, more powerful. We have deliberately
                            refused that temptation. The arms race of &ldquo;best AI&rdquo; is not a race
                            we want to run, because it frames the wrong question.
                        </p>
                        <p className="text-lg md:text-xl text-gray-700 dark:text-white/80 leading-relaxed font-medium">
                            The right question is: <em>does this make the student better?</em>
                        </p>
                        <p className="text-lg md:text-xl text-gray-700 dark:text-white/80 leading-relaxed font-medium">
                            That single question governs every feature decision, every prompt
                            we write, and every guardrail we put in place. If a feature
                            builds the student&apos;s own capability (their clarity, their
                            structure, their ethical reasoning), we build it. If it
                            outsources their thinking, we don&apos;t.
                        </p>
                    </div>
                </section>

                {/* ── SECTION: THE REAL COMPETITION ── */}
                <section className="py-16 md:py-24 bg-slate-50 dark:bg-[#030a1c] border-y border-gray-100 dark:border-white/[0.05]">
                    <div className="max-w-3xl mx-auto px-6">
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">
                            The real competition isn&apos;t &ldquo;better AI&rdquo;. It&apos;s better learning.
                        </h2>
                        <div className="space-y-6 text-lg text-gray-700 dark:text-white/80 leading-relaxed font-medium">
                            <p>
                                Other platforms compete on model benchmarks. We compete on
                                outcomes that matter to a law student&apos;s career: the ability to
                                construct a clear legal argument under exam conditions; the
                                discipline to write precisely when anxiety is high; the
                                confidence to walk into a seminar ready to test your own
                                understanding, not just repeat what a summary says about it.
                            </p>
                            <p>
                                Those outcomes require practice, not automation. They require
                                structured feedback, not generated answers. They require a
                                student who has genuinely wrestled with the material and
                                emerged with a stronger legal mind.
                            </p>
                            <p>
                                That&apos;s the competition we care about. And that&apos;s what we&apos;re
                                building for.
                            </p>
                        </div>
                    </div>
                </section>

                {/* ── SECTION: WHAT WE OPTIMISE FOR ── static classes, no dynamic Tailwind ── */}
                <section className="py-16 md:py-24">
                    <div className="max-w-5xl mx-auto px-6">
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-center">
                            What we optimise for
                        </h2>
                        <p className="text-center text-gray-500 dark:text-white/50 mb-16 text-lg">
                            Six student outcomes that guide every product decision.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

                            {/* Clarity */}
                            <div className="p-8 rounded-[2rem] border border-gray-200/60 dark:border-white/[0.08] bg-white dark:bg-white/[0.02] hover:shadow-lg transition-all duration-300">
                                <div className="w-12 h-12 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 flex items-center justify-center mb-6">
                                    <BookOpen className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                                </div>
                                <h3 className="text-xl font-bold mb-3">Clarity</h3>
                                <p className="text-gray-500 dark:text-white/60 leading-relaxed text-sm font-medium">
                                    Students who read fast but understand poorly will always underperform. We prioritise comprehension, not consumption.
                                </p>
                            </div>

                            {/* Structure */}
                            <div className="p-8 rounded-[2rem] border border-gray-200/60 dark:border-white/[0.08] bg-white dark:bg-white/[0.02] hover:shadow-lg transition-all duration-300">
                                <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20 flex items-center justify-center mb-6">
                                    <TrendingUp className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                                </div>
                                <h3 className="text-xl font-bold mb-3">Structure</h3>
                                <p className="text-gray-500 dark:text-white/60 leading-relaxed text-sm font-medium">
                                    Strong legal work follows framework. We drill structured legal reasoning (IRAC, issue-spotting, coherent argument flow) until it becomes familiar.
                                </p>
                            </div>

                            {/* Confidence */}
                            <div className="p-8 rounded-[2rem] border border-gray-200/60 dark:border-white/[0.08] bg-white dark:bg-white/[0.02] hover:shadow-lg transition-all duration-300">
                                <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-500/10 border border-purple-100 dark:border-purple-500/20 flex items-center justify-center mb-6">
                                    <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                                </div>
                                <h3 className="text-xl font-bold mb-3">Confidence</h3>
                                <p className="text-gray-500 dark:text-white/60 leading-relaxed text-sm font-medium">
                                    Genuine confidence comes from deliberate practice, not reassurance. We create conditions for students to trust their own legal thinking.
                                </p>
                            </div>

                            {/* Consistency */}
                            <div className="p-8 rounded-[2rem] border border-gray-200/60 dark:border-white/[0.08] bg-white dark:bg-white/[0.02] hover:shadow-lg transition-all duration-300">
                                <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 flex items-center justify-center mb-6">
                                    <CheckCircle className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                                </div>
                                <h3 className="text-xl font-bold mb-3">Consistency</h3>
                                <p className="text-gray-500 dark:text-white/60 leading-relaxed text-sm font-medium">
                                    Intermittent effort rarely supports confident exam preparation. Consistent daily practice, even five minutes, compounds into steadier revision.
                                </p>
                            </div>

                            {/* Integrity */}
                            <div className="p-8 rounded-[2rem] border border-gray-200/60 dark:border-white/[0.08] bg-white dark:bg-white/[0.02] hover:shadow-lg transition-all duration-300">
                                <div className="w-12 h-12 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 flex items-center justify-center mb-6">
                                    <Shield className="w-6 h-6 text-rose-600 dark:text-rose-400" />
                                </div>
                                <h3 className="text-xl font-bold mb-3">Integrity</h3>
                                <p className="text-gray-500 dark:text-white/60 leading-relaxed text-sm font-medium">
                                    A law career is built on ethical foundations. Our platform treats academic integrity not as a box-tick but as a core design constraint.
                                </p>
                            </div>

                            {/* Independence */}
                            <div className="p-8 rounded-[2rem] border border-gray-200/60 dark:border-white/[0.08] bg-white dark:bg-white/[0.02] hover:shadow-lg transition-all duration-300">
                                <div className="w-12 h-12 rounded-xl bg-sky-50 dark:bg-sky-500/10 border border-sky-100 dark:border-sky-500/20 flex items-center justify-center mb-6">
                                    <Users className="w-6 h-6 text-sky-600 dark:text-sky-400" />
                                </div>
                                <h3 className="text-xl font-bold mb-3">Independence</h3>
                                <p className="text-gray-500 dark:text-white/60 leading-relaxed text-sm font-medium">
                                    The goal is a student who no longer needs us. We build reliance on their own reasoning, not on our platform.
                                </p>
                            </div>

                        </div>
                    </div>
                </section>

                {/* ── SECTION: AI AS QUIET SUPPORT ── */}
                <section className="py-16 md:py-24 bg-slate-50 dark:bg-[#030a1c] border-y border-gray-100 dark:border-white/[0.05]">
                    <div className="max-w-3xl mx-auto px-6">
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">
                            AI as quiet support
                        </h2>
                        <div className="space-y-6 text-lg text-gray-700 dark:text-white/80 leading-relaxed font-medium">
                            <p>
                                We do use AI, but we use it the way a good tutor uses a
                                whiteboard: as a scaffold, not a crutch. Our AI components act
                                as a <strong>coach</strong> (asking the right questions),
                                a <strong>practice partner</strong> (giving you something to
                                argue against), and a <strong>feedback mirror</strong>{" "}
                                (showing you where your reasoning gaps are), never as an
                                author.
                            </p>
                            <p>
                                When AI is in the room, the spotlight stays on the student.
                                &ldquo;AI support, designed to keep you learning, not outsourcing.&rdquo;
                            </p>
                            <p>
                                We deliberately avoid: generating model essays, suggesting
                                specific case analyses on assessed work, or doing anything that
                                would allow a student to substitute AI output for their own
                                reasoning. The temptation to build those features is real. We
                                resist it.
                            </p>
                        </div>

                        {/* Callout */}
                        <div className="mt-12 p-8 rounded-2xl border border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/50 dark:bg-indigo-500/5">
                            <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-200 text-center leading-tight">
                                &ldquo;{dna.productEthos}&rdquo;
                            </p>
                        </div>
                    </div>
                </section>

                {/* ── SECTION: ETHICS BY DESIGN ── */}
                <section className="py-16 md:py-24">
                    <div className="max-w-3xl mx-auto px-6">
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">
                            Ethics by design
                        </h2>
                        <div className="space-y-6 text-lg text-gray-700 dark:text-white/80 leading-relaxed font-medium">
                            <p>
                                Academic integrity is not a policy document we appended at
                                launch. It is a design constraint baked into every feature from
                                the beginning. We do not generate assessed content. We do not
                                allow voice coaching on live assessment questions. We maintain
                                transparent guardrails, and we explain them clearly to
                                students.
                            </p>
                            <p>
                                We also believe students deserve to understand <em>why</em>{" "}
                                these boundaries exist, not just be told they do. Our{" "}
                                <Link
                                    href="/legal/academic-integrity"
                                    className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline"
                                >
                                    Academic Integrity Guide
                                </Link>{" "}
                                explains the boundaries clearly, and our platform reinforces
                                good scholarly habits rather than undermining them.
                            </p>
                            <p>
                                If you are a law student, you are training to uphold the
                                law. Your study tools should hold themselves to the same
                                standard.
                            </p>
                        </div>
                    </div>
                </section>

                {/* ── SECTION: ETHICAL MONETISATION ── */}
                <section className="py-16 md:py-24 border-t border-gray-100 dark:border-white/[0.05]">
                    <div className="max-w-3xl mx-auto px-6">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[10px] font-black uppercase tracking-[0.2em] border border-emerald-100 dark:border-emerald-500/20 mb-6">
                            <Scale size={14} />
                            Ethical Monetisation
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">
                            We sell support, not shortcuts.
                        </h2>
                        <div className="space-y-6 text-lg text-gray-700 dark:text-white/80 leading-relaxed font-medium">
                            <p>
                                The way a platform makes money tells you what it really
                                values. Many AI study tools have drifted toward a
                                pay-to-perform model: better answers for paying users,
                                premium grades behind a paywall, exclusive academic
                                advantage for those who can afford it. We refuse that
                                direction permanently.
                            </p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                                {MONETISATION_TAGLINES.NO_PAY_TO_LEARN_BETTER}
                            </p>
                            <p>
                                Every subscribed Durmah student receives the full
                                academic capability of the platform: same reasoning
                                quality, same lectures, same assignment support, same
                                research tools, same Durmah assistant. Academic
                                capability is a floor, not a ceiling. We do not sell
                                academic advantage, and we will not.
                            </p>
                        </div>

                        {/* What we will never sell */}
                        <div className="mt-10 p-8 rounded-2xl border border-rose-100 dark:border-rose-500/20 bg-rose-50/40 dark:bg-rose-500/5">
                            <h3 className="text-sm font-black uppercase tracking-[0.2em] text-rose-700 dark:text-rose-300 mb-4">
                                What we will never sell
                            </h3>
                            <ul className="space-y-2 text-base text-gray-700 dark:text-white/80 font-medium">
                                {MONETISATION_REFUSALS.map((line) => (
                                    <li key={line} className="flex items-start gap-2">
                                        <span className="text-rose-500 mt-1 flex-shrink-0">✗</span>
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* What we may, in future, charge for */}
                        <div className="mt-6 p-8 rounded-2xl border border-emerald-100 dark:border-emerald-500/20 bg-emerald-50/40 dark:bg-emerald-500/5">
                            <h3 className="text-sm font-black uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300 mb-2">
                                What we may charge for
                            </h3>
                            <p className="text-sm text-emerald-900/70 dark:text-emerald-200/70 mb-6 font-medium">
                                Today there is one Full Access plan with monthly and annual options. Any future paid layers will only ever sit in these four areas, never in academic capability.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {PERMITTED_MONETISATION_LAYERS.map((layer) => (
                                    <div
                                        key={layer.title}
                                        className="rounded-xl bg-white/70 dark:bg-white/[0.03] border border-emerald-100/60 dark:border-emerald-500/10 p-4"
                                    >
                                        <div className="text-sm font-black text-gray-900 dark:text-white mb-1">
                                            {layer.title}
                                        </div>
                                        <div className="text-sm text-gray-600 dark:text-white/60 leading-relaxed">
                                            {layer.body}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <p className="mt-10 text-center text-sm font-bold uppercase tracking-[0.2em] text-gray-400 dark:text-white/40">
                            {MONETISATION_TAGLINES.BUILT_ON_FAIRNESS}
                        </p>
                    </div>
                </section>

                {/* ── SECTION: BUILT TO TRAVEL ── */}
                <section className="py-16 md:py-24 bg-slate-50 dark:bg-[#030a1c] border-y border-gray-100 dark:border-white/[0.05]">
                    <div className="max-w-3xl mx-auto px-6">
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">
                            Built to travel across universities
                        </h2>
                        <div className="space-y-6 text-lg text-gray-700 dark:text-white/80 leading-relaxed font-medium">
                            <p>
                                Our philosophy scales precisely because it is not tied to one
                                institution&apos;s syllabus or one country&apos;s curriculum. Student
                                skill-building, ethical learning, and structured practice are
                                universal goods. They apply whether you are reading law at
                                Durham, Oxford, UCL, or a law school on the other side of the
                                world.
                            </p>
                            <p>
                                Supported university packs adapt content to available
                                modules, term structure, and study context. The
                                philosophy underneath never changes. The student is always the
                                hero. The AI is always quiet support.
                            </p>
                            <p>
                                We are expanding incrementally and with care, ensuring that
                                every university-aware route maintains the same integrity
                                standards our first students experienced.
                            </p>
                        </div>

                        <div className="mt-10 flex flex-wrap gap-3">
                            {[
                                { label: "LNAT Preparation", href: "/lnat-preparation" },
                                { label: "Academic Integrity", href: "/legal/academic-integrity" },
                                { label: "Ethics Policy", href: "/legal/ethics" },
                                { label: "About Durmah", href: "/about" },
                                { label: "Pricing", href: "/pricing" },
                            ].map(({ label, href }) => (
                                <Link
                                    key={href}
                                    href={href}
                                    className="px-4 py-2 rounded-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm font-medium text-gray-700 dark:text-white/70 hover:border-indigo-300 dark:hover:border-indigo-500/40 hover:text-indigo-700 dark:hover:text-indigo-300 transition-all"
                                >
                                    {label}
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── FAQ ── */}
                <section className="py-16 md:py-24">
                    <div className="max-w-3xl mx-auto px-6">
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-center">
                            Common questions
                        </h2>
                        <p className="text-center text-gray-500 dark:text-white/50 mb-12 text-lg">
                            Honest answers to the questions students ask most.
                        </p>
                        <div className="space-y-4">
                            {FAQ_ITEMS.map((item) => (
                                <FAQItem key={item.q} q={item.q} a={item.a} />
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── CLOSING CTA ── */}
                <section className="py-24 md:py-32 border-t border-gray-100 dark:border-white/[0.05] text-center px-6">
                    <p className="text-sm font-bold uppercase tracking-[0.2em] text-gray-400 dark:text-white/40 mb-6">
                        Ready when you are
                    </p>
                    <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-8 leading-tight">
                        Start learning. <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-indigo-400">
                            In earnest.
                        </span>
                    </h2>
                    <p className="text-lg md:text-xl text-gray-500 dark:text-white/60 max-w-xl mx-auto mb-12 leading-relaxed font-medium">
                        No credit card required for the trial. No commitments until you
                        know it genuinely helps you learn better.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link href="/login?intent=signup" className="h-14 px-10 inline-flex items-center justify-center rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-950 font-bold text-sm tracking-wide transition-all hover:scale-[1.02] hover:shadow-xl active:scale-95 shadow-lg">
                                Start Free Trial
                            </Link>
                        <Link href="/" className="h-14 px-10 inline-flex items-center gap-2 rounded-full bg-white dark:bg-white/5 text-gray-900 dark:text-white border border-gray-200 dark:border-white/10 font-bold text-sm tracking-wide hover:bg-gray-50 dark:hover:bg-white/10 transition-all">
                                See the platform <ArrowRight className="w-4 h-4" />
                            </Link>
                    </div>
                </section>
            </main>
        </>
    );
}
